//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import moment from "moment";
import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Menu } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { EverestInvoice as ApiEverestInvoice, EverestInvoicePaymentState as ApiEverestInvoicePaymentState } from "../../api/billing/v1/ibilling";
import Auth from "../../auth/Auth";
import { ContentSegment, ErrorMessage, Field, FieldContent as FC, FieldLabelWide as FL, FieldSet, Processing, SecondaryMenu, TextLink } from "../../ui/lib";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import { Routes } from "../../routes";
import { DownloadEverestInvoice } from "./DownloadEverestInvoice";

// The API returns enum values as strings in JSON, while the generated TS enum is numeric.
const normalizePaymentState = (state?: ApiEverestInvoicePaymentState | string | null): ApiEverestInvoicePaymentState | undefined => {
  if (state == null || state === "") {
    return ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNSPECIFIED;
  }
  if (typeof state === "number") {
    return state;
  }
  const stateFromEnumName = ApiEverestInvoicePaymentState[state as keyof typeof ApiEverestInvoicePaymentState];
  if (typeof stateFromEnumName === "number") {
    return stateFromEnumName;
  }
  const numericState = Number(state);
  if (Number.isFinite(numericState)) {
    return numericState;
  }
  return undefined;
};

const paymentStateLabel = (inv: ApiEverestInvoice): string => {
  if (inv.is_overdue) {
    return "Overdue";
  }
  switch (normalizePaymentState(inv.payment_state)) {
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_UNPAID:
      return "Unpaid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PARTIALLY_PAID:
      return "Partially paid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_PAID_IN_FULL:
      return "Paid";
    case ApiEverestInvoicePaymentState.EVEREST_INVOICE_PAYMENT_STATE_CREDITED_IN_FULL:
      return "Credited";
    default:
      return "Unknown";
  }
};

interface IEverestInvoiceDetailsProps extends IWithRefreshProps, RouteComponentProps {
  auth: Auth;
  onOrganizationSelected: (id: string) => void;
}

interface IEverestInvoiceDetailsState {
  invoiceId?: string;
  invoice?: ApiEverestInvoice;
  errorMessage?: string;
  downloading: boolean;
}

class EverestInvoiceDetails extends Component<IEverestInvoiceDetailsProps, IEverestInvoiceDetailsState> {
  state: IEverestInvoiceDetailsState = { downloading: false };

  reloadInvoice = async () => {
    const invoice = await apiClients.idashboardClient.GetEverestInvoice({ id: this.state.invoiceId });
    this.setState({ invoice });
  };

  componentDidMount() {
    const params = this.props.match.params as { invoiceId?: string };
    this.setState({ invoiceId: params.invoiceId }, () => {
      this.props.refreshWithTimer && this.props.refreshWithTimer(this.reloadInvoice, 60000);
    });
  }

  onDownload = async () => {
    try {
      this.setState({ downloading: true, errorMessage: undefined });
      await DownloadEverestInvoice(this.state.invoiceId || "", this.props.auth, this.state.invoice?.invoice_date, this.state.invoice?.invoice_number);
    } catch (e) {
      this.setState({ errorMessage: e });
    } finally {
      this.setState({ downloading: false });
    }
  };

  render() {
    const inv = this.state.invoice || {};
    const fmtAmount = (a?: { amount?: string; currency?: string }) => (a && a.amount ? `${a.amount} ${a.currency || ""}` : "-");
    const fmtDate = (d?: Date | string) => (d ? moment(d).format("YYYY-MM-DD") : "-");
    return (
      <ContentSegment>
        <ErrorMessage active={!!this.state.errorMessage} onDismiss={() => this.setState({ errorMessage: undefined })} message={this.state.errorMessage} />
        <Processing active={this.state.downloading} message="Downloading, please wait..." />
        <SecondaryMenu>
          <Menu.Item header>Everest Invoice {inv.invoice_number}</Menu.Item>
        </SecondaryMenu>
        <FieldSet>
          <Field>
            <FL>Number</FL>
            <FC>{inv.invoice_number || "-"}</FC>
          </Field>
          <Field>
            <FL>Organization</FL>
            <FC>
              <TextLink href={Routes.dashboard_sales_organization_detailsWithId(inv.organization_id || "")}>{inv.organization_id}</TextLink>
            </FC>
          </Field>
          <Field>
            <FL>Invoice date</FL>
            <FC>{fmtDate(inv.invoice_date)}</FC>
          </Field>
          <Field>
            <FL>Due date</FL>
            <FC>{fmtDate(inv.due_date)}</FC>
          </Field>
          <Field>
            <FL>Total</FL>
            <FC>{fmtAmount(inv.total_amount)}</FC>
          </Field>
          <Field>
            <FL>Amount due</FL>
            <FC>{fmtAmount(inv.total_amount_due)}</FC>
          </Field>
          <Field>
            <FL>Payments applied</FL>
            <FC>{fmtAmount(inv.payments_applied)}</FC>
          </Field>
          <Field>
            <FL>Credited</FL>
            <FC>{fmtAmount(inv.total_credited)}</FC>
          </Field>
          <Field>
            <FL>Tax</FL>
            <FC>{fmtAmount(inv.total_tax)}</FC>
          </Field>
          <Field>
            <FL>State</FL>
            <FC>{paymentStateLabel(inv)}</FC>
          </Field>
          <Field>
            <FL>Last synced</FL>
            <FC>{inv.last_synced_at ? moment(inv.last_synced_at).format() : "-"}</FC>
          </Field>
        </FieldSet>
        <Button content="Download PDF" icon="download" onClick={this.onDownload} />
      </ContentSegment>
    );
  }
}

export default withRefresh()(EverestInvoiceDetails);
